import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./calendarStyles.css";
import { useRef, useMemo, useState, useEffect } from "react";
import CalendarNext from "../../../assets/svg/Calendar/CalendarNext";
import CalendarPrevious from "../../../assets/svg/Calendar/CalendarPrevious";
import moment from "moment";
import debounce from "lodash/debounce";

interface CustomCalendarProps {
  readonly onChange: (date: any) => void;
  readonly value?: string | Date | null;
}

function CustomCalendar({
  onChange,
  value,
}: CustomCalendarProps): JSX.Element {
  const calendarRef = useRef<any>();
  const [dayInput, setDayInput] = useState<string>("");
  const [monthInput, setMonthInput] = useState<string>("");
  const [yearInput, setYearInput] = useState<string>("");

  // Parse the value prop to a Date object
  // Supports both "MM-DD-YYYY" string format and Date objects
  const parsedValue = useMemo(() => {
    if (!value) return undefined;
    
    if (value instanceof Date) {
      return value;
    }
    
    // Try to parse as "MM-DD-YYYY" format
    if (typeof value === "string") {
      const parsed = moment(value, "MM-DD-YYYY", true);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
      // Try other common formats
      const altParsed = moment(value);
      if (altParsed.isValid()) {
        return altParsed.toDate();
      }
    }
    
    return undefined;
  }, [value]);

  // Sync inputs with value prop
  useEffect(() => {
    if (parsedValue) {
      const date = moment(parsedValue);
      setDayInput(date.format("DD"));
      setMonthInput(date.format("MM"));
      setYearInput(date.format("YYYY"));
      setInternalDate(parsedValue);
    } else {
      setDayInput("");
      setMonthInput("");
      setYearInput("");
      setInternalDate(null);
    }
  }, [parsedValue]);

  // Build date progressively as user types
  const buildDate = (day: string, month: string, year: string): Date | null => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();

    // Progressive building: use current values for missing parts
    let finalYear = year ? Number.parseInt(year, 10) : currentYear;
    let finalMonth = month ? Number.parseInt(month, 10) - 1 : currentMonth; // Month is 0-indexed
    let finalDay = day ? Number.parseInt(day, 10) : currentDay;

    // Validate ranges before creating date
    if (month && (finalMonth < 0 || finalMonth > 11)) return null;
    if (day && (finalDay < 1 || finalDay > 31)) return null;
    if (year && (finalYear < 1900 || finalYear > 2100)) return null;

    // Create date with progressive building
    // If month is entered first, use current year and day
    // Then update day when entered, then year when entered
    const date = new Date(
      finalYear,
      finalMonth,
      finalDay
    );

    // Validate the date (e.g., Feb 30 is invalid)
    // Only validate if all parts are provided
    if (day && month && year) {
      if (
        date.getFullYear() !== finalYear ||
        date.getMonth() !== finalMonth ||
        date.getDate() !== finalDay
      ) {
        return null;
      }
    }

    // Don't allow future dates
    if (date > now) return null;

    return date;
  };

  // Internal state to track calendar value without triggering onChange
  const [internalDate, setInternalDate] = useState<Date | null>(null);

  // Debounced update function - waits for user to finish typing
  const debouncedUpdateDate = useMemo(
    () =>
      debounce((day: string, month: string, year: string) => {
        const date = buildDate(day, month, year);
        if (date && date <= new Date()) {
          // Only call onChange if all three fields are complete
          if (day && month && year && day.length >= 2 && month.length >= 2 && year.length === 4) {
            onChange(date);
          }
        }
      }, 500),
    [onChange]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateDate.cancel();
    };
  }, [debouncedUpdateDate]);

  // Update internal date immediately for visual feedback, but debounce the calendar update
  const updateInternalDate = (day: string, month: string, year: string) => {
    const date = buildDate(day, month, year);
    if (date && date <= new Date()) {
      setInternalDate(date);
    } else {
      setInternalDate(null);
    }
    // Debounce the onChange call
    debouncedUpdateDate(day, month, year);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, ""); // Only digits
    if (val.length > 2) val = val.slice(0, 2);
    
    // If all inputs are present and user modifies day, reset all fields
    const allFieldsComplete = 
      dayInput.length >= 2 && 
      monthInput.length >= 2 && 
      yearInput.length === 4;
    
    if (allFieldsComplete && val !== dayInput) {
      // User is modifying a complete date - reset all fields
      setDayInput(val);
      setMonthInput("");
      setYearInput("");
      setInternalDate(null);
      debouncedUpdateDate.cancel();
    } else {
      setDayInput(val);
      updateInternalDate(val, monthInput, yearInput);
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, ""); // Only digits
    if (val.length > 2) val = val.slice(0, 2);
    
    // If all inputs are present and user modifies month, reset all fields
    const allFieldsComplete = 
      dayInput.length >= 2 && 
      monthInput.length >= 2 && 
      yearInput.length === 4;
    
    if (allFieldsComplete && val !== monthInput) {
      // User is modifying a complete date - reset all fields
      setDayInput("");
      setMonthInput(val);
      setYearInput("");
      setInternalDate(null);
      debouncedUpdateDate.cancel();
    } else {
      setMonthInput(val);
      updateInternalDate(dayInput, val, yearInput);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, ""); // Only digits
    if (val.length > 4) val = val.slice(0, 4);
    
    // If all inputs are present and user modifies year, reset all fields
    const allFieldsComplete = 
      dayInput.length >= 2 && 
      monthInput.length >= 2 && 
      yearInput.length === 4;
    
    if (allFieldsComplete && val !== yearInput) {
      // User is modifying a complete date - reset all fields
      setDayInput("");
      setMonthInput("");
      setYearInput(val);
      setInternalDate(null);
      debouncedUpdateDate.cancel();
    } else {
      setYearInput(val);
      updateInternalDate(dayInput, monthInput, val);
    }
  };

  const handleDayBlur = () => {
    if (dayInput?.length === 1) {
      const padded = dayInput.padStart(2, "0");
      setDayInput(padded);
      updateInternalDate(padded, monthInput, yearInput);
    } else if (dayInput && dayInput.length === 2) {
      // Check if complete date and trigger onChange
      if (monthInput && monthInput.length >= 2 && yearInput && yearInput.length === 4) {
        const date = buildDate(dayInput, monthInput, yearInput);
        if (date && date <= new Date()) {
          onChange(date);
        }
      }
    }
  };

  const handleMonthBlur = () => {
    if (monthInput?.length === 1) {
      const padded = monthInput.padStart(2, "0");
      setMonthInput(padded);
      updateInternalDate(dayInput, padded, yearInput);
    } else if (monthInput && monthInput.length === 2) {
      // Check if complete date and trigger onChange
      if (dayInput && dayInput.length >= 2 && yearInput && yearInput.length === 4) {
        const date = buildDate(dayInput, monthInput, yearInput);
        if (date && date <= new Date()) {
          onChange(date);
        }
      }
    }
  };

  const handleYearBlur = () => {
    // When year is complete (4 digits), check if date is complete and trigger onChange
    if (yearInput && yearInput.length === 4) {
      if (dayInput && dayInput.length >= 2 && monthInput && monthInput.length >= 2) {
        const date = buildDate(dayInput, monthInput, yearInput);
        if (date && date <= new Date()) {
          onChange(date);
        }
      }
    }
  };

  return (
    <div className="custom-calendar-wrapper">
      <Calendar
        ref={calendarRef}
        value={internalDate || parsedValue}
        maxDate={new Date()}
        selectRange={false}
        formatShortWeekday={(locale, date) => {
          const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sat"];
          return dayNames[moment(date).day()] || "";
        }}
        nextLabel={<CalendarNext />}
        prevLabel={<CalendarPrevious />}
        next2Label={<span className="calendar-double-arrow">»</span>}
        prev2Label={<span className="calendar-double-arrow">«</span>}
        showFixedNumberOfWeeks={false}
        showWeekNumbers={false}
        tileClassName={() => {
          // Only header weekdays should be styled as weekend, not the date tiles
          return "";
        }}
        className="custom-calendar"
        onChange={(date) => onChange(date)}
      />
      {/* Date Input Fields - positioned between navigation and weekdays */}
      <div className="calendar-date-inputs">
        <input
          id="calendar-day"
          type="text"
          value={dayInput}
          onChange={handleDayChange}
          onBlur={handleDayBlur}
          placeholder="DD"
          maxLength={2}
          className="calendar-input"
        />
        <input
          id="calendar-month"
          type="text"
          value={monthInput}
          onChange={handleMonthChange}
          onBlur={handleMonthBlur}
          placeholder="MM"
          maxLength={2}
          className="calendar-input"
        />
        <input
          id="calendar-year"
          type="text"
          value={yearInput}
          onChange={handleYearChange}
          onBlur={handleYearBlur}
          placeholder="YYYY"
          maxLength={4}
          className="calendar-input"
        />
      </div>
    </div>
  );
}

export default CustomCalendar;
